import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { Kind, useGetSourceWithTypeQuery } from "../../../graphql/generated";
import { BPSource } from "../../../utils/classes";
import { EditResourceDialog } from "../../ResourceDialog/EditResourceDialog";

interface EditSourceProps {
  name: string;
  onSaveSuccess: () => void;
  onCancel: () => void;
  readOnly?: boolean;
}

export const EditSourceDialog: React.FC<EditSourceProps> = ({
  name,
  readOnly,
  onSaveSuccess,
  onCancel,
}) => {
  const [open, setOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { data, error } = useGetSourceWithTypeQuery({
    variables: {
      name,
    },
    fetchPolicy: "network-only",
  });

  // Communicate error if present.
  useEffect(() => {
    if (error != null) {
      enqueueSnackbar(`Error retrieving data for source ${name}.`, {
        variant: "error",
      });
      console.error(error);
    }
  }, [enqueueSnackbar, error, name]);

  // Open dialog when we have data.
  useEffect(() => {
    if (data != null) {
      setOpen(true);
    }
  }, [data, setOpen]);

  async function handleSave(values: { [key: string]: any }) {
    const source = new BPSource(data?.sourceWithType.source!);
    source.setParamsFromMap(values);

    source.metadata.displayName = values.displayName;

    try {
      await source.apply();
      enqueueSnackbar("Saved source!", { variant: "success" });
      setOpen(false);
      onSaveSuccess();
    } catch (err) {
      enqueueSnackbar("Error saving source", { variant: "error" });
      console.error(err);
    }
  }

  function handleClose() {
    setOpen(false);
    onCancel();
  }

  return (
    <EditResourceDialog
      fullWidth
      resourceTypeDisplayName={data?.sourceWithType.source?.metadata.name ?? ""}
      displayName={data?.sourceWithType.source?.metadata.displayName ?? ""}
      description={data?.sourceWithType.sourceType?.metadata.description ?? ""}
      onSave={handleSave}
      onClose={handleClose}
      onCancel={handleClose}
      additionalInfo={data?.sourceWithType.sourceType?.metadata.additionalInfo}
      resourceDocLink={
        data?.sourceWithType.sourceType?.metadata.resourceDocLink ?? ""
      }
      parameters={data?.sourceWithType.source?.spec.parameters ?? []}
      parameterDefinitions={
        data?.sourceWithType.sourceType?.spec.parameters ?? []
      }
      paused={data?.sourceWithType.source?.spec.disabled}
      kind={Kind.Source}
      open={open}
      readOnly={readOnly}
    />
  );
};
